import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Trash2, Loader2, Car, Calendar, StickyNote } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { whatsappLink } from "@/lib/contact";

const STATUS = {
  new: { label: "Yeni", cls: "bg-gold/15 text-gold" },
  contacted: { label: "İletişime Geçildi", cls: "bg-blue-500/15 text-blue-300" },
  done: { label: "Tamamlandı", cls: "bg-green-500/15 text-green-400" },
  cancelled: { label: "İptal", cls: "bg-neutral-700/40 text-neutral-400" },
};

export default function AppointmentsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const settings = useSettings();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/appointments");
      setItems(data);
    } catch {
      toast.error("Randevular yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function setStatus(id, status) {
    try {
      await api.put(`/admin/appointments/${id}`, { status });
      setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch { toast.error("Güncellenemedi."); }
  }

  async function remove(id) {
    if (!window.confirm("Randevu talebini silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/admin/appointments/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Silindi.");
    } catch { toast.error("Silinemedi."); }
  }

  return (
    <div data-testid="admin-appointments">
      <h1 className="font-display text-2xl font-medium text-white sm:text-3xl">Randevu Talepleri</h1>
      <p className="mt-1 text-sm text-neutral-500">{items.length} talep</p>

      {loading ? (
        <div className="mt-8 flex items-center justify-center py-16 text-neutral-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>
      ) : items.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-white/10 py-16 text-center text-neutral-500">Henüz randevu talebi yok.</div>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {items.map((a) => {
            const st = STATUS[a.status] || STATUS.new;
            return (
              <div key={a.id} data-testid={`appointment-${a.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-lg font-medium text-white">{a.name}</h3>
                    <a href={`tel:${a.phone}`} className="mt-1 flex items-center gap-1.5 text-sm text-gold"><Phone className="h-3.5 w-3.5" /> {a.phone}</a>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${st.cls}`}>{st.label}</span>
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-neutral-300">
                  {a.car && <p className="flex items-center gap-2"><Car className="h-4 w-4 text-neutral-500" /> {a.car}</p>}
                  {a.service && <p className="flex items-center gap-2"><StickyNote className="h-4 w-4 text-neutral-500" /> {a.service}</p>}
                  {a.preferred_day && <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-neutral-500" /> {a.preferred_day}</p>}
                  {a.note && <p className="rounded-lg border border-white/10 bg-neutral-900 px-3 py-2 text-neutral-400">{a.note}</p>}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <select value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} data-testid={`appt-status-${a.id}`} className="rounded-lg border border-white/10 bg-neutral-900 px-3 py-2 text-xs text-white focus:border-gold focus:outline-none">
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <a href={whatsappLink(settings, `Merhaba ${a.name}, Meguiar's İzmit'ten ulaşıyoruz. Randevu talebiniz için yazıyoruz.`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366]/15 px-3 py-2 text-xs font-semibold text-[#25D366] hover:bg-[#25D366]/25 transition-colors">
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                  <button onClick={() => remove(a.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-neutral-400 hover:border-red-500/40 hover:text-red-400 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" /> Sil
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
